import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, makeStyles } from '@material-ui/core';

import { Organization } from '../../types/organization.type';
import { useFindOrders } from '../../../orders/hooks/get-orders.hook';
import { OrderTableWithDetails } from '../../../orders/components/Tables/OrderTableWithDetails.component';
import { mapOrderToTableRow } from '../../../orders/utils/map-order-to-table-row.util';
import { ORDER_TABLE_HEADERS } from '../../../orders/constants/order-table-headers.constant';
import { ORGANIZATION_CREATE_ORDER_PATH } from '../../constants/organizations-paths.constants';

type Props = {
  organization: Organization;
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },

  header: {
    display: 'flex',
    justifyContent: 'flex-end',
  },

  createOrderButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

export default function OrganizationOrdersTab({ organization }: Props) {
  const classes = useStyles();
  const history = useHistory();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { loading, orders, totalCount } = useFindOrders({
    page: page + 1,
    limit: rowsPerPage,
    sortDirection: 'desc',
    filters: { organizationId: organization.id },
  });

  const tableRows = useMemo(() => orders.map(mapOrderToTableRow), [orders]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  return (
    <section className={classes.root}>
      <div className={classes.header}>
        <Button
          size="small"
          variant="contained"
          className={classes.createOrderButton}
          disabled={(organization.deliveryPoints ?? []).length === 0}
          onClick={() => history.push(ORGANIZATION_CREATE_ORDER_PATH(organization.id))}
        >
          Créer
        </Button>
      </div>
      <OrderTableWithDetails
        maxHeight="60svh"
        headers={ORDER_TABLE_HEADERS}
        orders={tableRows}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        isLoading={loading}
        rowsPerPageOptions={[10, 25, 50]}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </section>
  );
}
